import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { tenant } from '../../tenants/tenant';

/**
 * Helper function to convert hex to HSL for PrimeNG primitive colors
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Custom PrimeNG theme preset with custom primary color #003B55
 * 
 * The issue is that PrimeNG uses `primitive` colors (HSL format) as the base,
 * and then `semantic` colors reference those primitives. The palette structure
 * (50-950) is for Tailwind CSS. To ensure the 500 values are used, we need to
 * map them to the `primitive` color structure.
 */
const baseTheme = tenant.theme as any;
const baseAura = Aura as any;

export const NexaAppTheme = definePreset(Aura, {
  ...baseTheme,
  components: {
    avatar: {
      xl: {
        width: '3.5rem',
        height: '3.5rem',
        fontSize: '2rem',
        icon: {
          size: '2rem',
        },
        group: {
          offset: '-1.5rem',
        },
      },
    },
    ...baseTheme.components,
  },
  primitive: {
    ...baseAura.primitive,
    // Override primitive colors to use the 500 values from semantic palette
    // This ensures PrimeNG uses #003B55 (primary-500) instead of #80b2bf (primary-200)
    primary: {
      ...baseAura.primitive?.primary,
      ...hexToHsl(baseTheme.semantic?.primary?.[500] || '#003B55'),
    },
    secondary: {
      ...baseAura.primitive?.secondary,
      ...hexToHsl(baseTheme.semantic?.secondary?.[500] || '#555555'),
    },
    danger: {
      ...baseAura.primitive?.danger,
      ...hexToHsl(baseTheme.semantic?.danger?.[500] || '#74262B'),
    },
  },
});
